import {
  createContext,
  useContext,
  createSignal,
  createEffect,
  untrack,
} from "solid-js";

import { useAuthn } from "@uic/domain/authn";
import { setOrgMemberIdFn } from "@uic/domain/wsc";

import {
  fetchUserOrganizations,
  getSavedOrgMemberId,
  saveOrgMemberId,
  clearOrgMemberId,
} from "../core/organizations";

/**
 * @typedef {object} OrganizationMembership
 * @property {string} membershipId - The membership record id
 * @property {{id: string, name: string, slug: string}} organization - The organization details
 */

/**
 * @typedef {object} OrganizationsContextValue
 * @property {() => OrganizationMembership[]} memberships - User's organization memberships
 * @property {() => string|null} currentMembershipId - Currently selected membership id
 * @property {() => OrganizationMembership|undefined} getCurrentMembership - Get current membership details
 * @property {(membershipId: string) => void} selectOrganization - Select an organization
 * @property {() => boolean} isLoading - Whether orgs are being loaded
 * @property {() => Promise<void>} refresh - Reload organizations
 */

const OrganizationsContext = createContext();

/**
 * Hook to access the organizations context
 * @returns {OrganizationsContextValue} Object to help with organization actions
 */
export function useOrganizations() {
  const context = useContext(OrganizationsContext);
  if (!context) {
    throw new Error(
      "useOrganizations must be used within an OrganizationsProvider",
    );
  }
  return context;
}

/**
 * Organizations provider component
 * @param {object} props Component props
 * @param {import('solid-js').JSX.Element} props.children The children
 * @returns {import('solid-js').JSX.Element} The provider element
 */
export function OrganizationsProvider(props) {
  const { user, isAuthenticated } = useAuthn();

  const [memberships, setMemberships] = createSignal(
    /** @type {OrganizationMembership[]} */ ([]),
  );
  const [currentMembershipId, setCurrentMembershipId] = createSignal(
    /** @type {string|null} */ (null),
  );
  const [isLoading, setIsLoading] = createSignal(true);

  // Connect to axios interceptor
  setOrgMemberIdFn(() => untrack(() => currentMembershipId()));

  const loadOrganizations = async () => {
    if (!isAuthenticated()) {
      setMemberships([]);
      setCurrentMembershipId(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const orgs = await fetchUserOrganizations();
      setMemberships(orgs);

      // Select saved org or default to first
      const savedId = getSavedOrgMemberId();
      const validSaved = orgs.find((m) => m.membershipId === savedId);

      if (validSaved) {
        setCurrentMembershipId(savedId);
      } else if (orgs.length > 0) {
        setCurrentMembershipId(orgs[0].membershipId);
        saveOrgMemberId(orgs[0].membershipId);
      }
    } catch (error) {
      console.error("Failed to load organizations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const selectOrganization = (membershipId) => {
    const valid = memberships().find((m) => m.membershipId === membershipId);
    if (valid) {
      setCurrentMembershipId(membershipId);
      saveOrgMemberId(membershipId);
    }
  };

  const getCurrentMembership = () => {
    return memberships().find((m) => m.membershipId === currentMembershipId());
  };

  // Load orgs when user changes
  createEffect(() => {
    const u = user();
    if (u) {
      loadOrganizations();
    } else {
      setMemberships([]);
      setCurrentMembershipId(null);
      clearOrgMemberId();
      setIsLoading(false);
    }
  });

  const value = {
    memberships,
    currentMembershipId,
    getCurrentMembership,
    selectOrganization,
    isLoading,
    refresh: loadOrganizations,
  };

  return (
    <OrganizationsContext.Provider value={value}>
      {props.children}
    </OrganizationsContext.Provider>
  );
}
